import { safeJsonParseArray } from '~/shared/utils/safeJson';

import type {
  CoverageGrouping,
  DrillLocation,
  ExplorationAccountCache,
  ExplorationFilter,
  ExplorationSecondary,
  ExplorationState,
  DisplayMode,
  ViewMode,
} from '../types/exploration';
import { drillLocationsEqual, emptyDrillLocation } from './drill/drillLocation';
import { parseDrillLocation, serializeDrillLocation } from './drill/drillLocationUrl';

export const EXPLORATION_VIEW_PARAM = 'view';
export const EXPLORATION_FILTERS_PARAM = 'filters';
export const EXPLORATION_DISPLAY_PARAM = 'display';
export const EXPLORATION_GROUP_PARAM = 'group';
export const EXPLORATION_COVERAGE_GROUP_PARAM = 'coverageGroup';
export const EXPLORATION_TAG_KEY_PARAM = 'tagKey';
export const EXPLORATION_BY_ACCOUNT_PARAM = 'byAccount';
export const EXPLORATION_SEARCH_PARAM = 'q';
export const EXPLORATION_DRILL_PARAM = 'drill';
export const EXPLORATION_VOLUME_PARAM = 'volume';
export const EXPLORATION_ACCOUNT_PARAM = 'account';

const VIEW_MODES = new Set<ViewMode>(['exploration', 'coverage', 'activation']);
const DISPLAY_MODES = new Set<DisplayMode>(['bubbles', 'treemap', 'tree']);

/** Views exposed in the top bar — others fall back to the default view. */
const ENABLED_VIEW_MODES = new Set<ViewMode>(['exploration', 'activation']);
/** Display modes exposed in the top bar — others fall back to bubbles. */
const ENABLED_DISPLAY_MODES = new Set<DisplayMode>(['bubbles']);
const SECONDARY_MODES = new Set<ExplorationSecondary>(['cluster', 'region', 'tag', 'none']);
const COVERAGE_GROUPINGS = new Set<CoverageGrouping>(['hierarchy', 'region']);

const STRING_FILTER_TYPES = new Set([
  'cluster',
  'region',
  'instanceId',
  'volumeId',
  'accountId',
  'nodeGroup',
  'diskType',
  'activationStatus',
  'supportStatus',
  'infrastructure',
]);

export interface ExplorationUrlSnapshot {
  view: ViewMode;
  displayMode: DisplayMode;
  secondary: ExplorationSecondary;
  coverageGrouping: CoverageGrouping;
  tagKey: string;
  drillPath: DrillLocation;
  searchQuery: string;
  filters: ExplorationFilter[];
  groupByAccount: boolean;
  selectedVolumeId: string | null;
  accountId: string | null;
}

export const defaultExplorationUrlSnapshot: ExplorationUrlSnapshot = {
  view: 'exploration',
  displayMode: 'bubbles',
  secondary: 'none',
  coverageGrouping: 'hierarchy',
  tagKey: '',
  drillPath: emptyDrillLocation(),
  searchQuery: '',
  filters: [],
  groupByAccount: false,
  selectedVolumeId: null,
  accountId: null,
};

export type UrlSnapshotDelta = 'none' | 'search-only' | 'navigation';

export interface BuildExplorationSearchParamsOptions {
  defaultAccountId?: string | null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function parseView(raw: string | null): ViewMode {
  if (raw && VIEW_MODES.has(raw as ViewMode)) {
    return raw as ViewMode;
  }
  return defaultExplorationUrlSnapshot.view;
}

function parseDisplayMode(raw: string | null): DisplayMode {
  if (raw && DISPLAY_MODES.has(raw as DisplayMode)) {
    return raw as DisplayMode;
  }
  return defaultExplorationUrlSnapshot.displayMode;
}

function parseSecondary(raw: string | null): ExplorationSecondary {
  if (raw && SECONDARY_MODES.has(raw as ExplorationSecondary)) {
    return raw as ExplorationSecondary;
  }
  return defaultExplorationUrlSnapshot.secondary;
}

function parseCoverageGrouping(raw: string | null): CoverageGrouping {
  if (raw && COVERAGE_GROUPINGS.has(raw as CoverageGrouping)) {
    return raw as CoverageGrouping;
  }
  return defaultExplorationUrlSnapshot.coverageGrouping;
}

function parseExplorationFilter(raw: unknown): ExplorationFilter | null {
  if (!raw || typeof raw !== 'object') return null;

  const candidate = raw as Record<string, unknown>;
  const type = candidate.type;

  if (type === 'capacity') {
    const min = candidate.min;
    const max = candidate.max;
    if (typeof min !== 'number' || typeof max !== 'number' || !Number.isFinite(min) || !Number.isFinite(max)) {
      return null;
    }
    return { type: 'capacity', min, max };
  }

  if (type === 'tag') {
    const key = candidate.key;
    const value = candidate.value;
    if (!isNonEmptyString(key) || !isNonEmptyString(value)) return null;
    return { type: 'tag', key, value };
  }

  if (typeof type === 'string' && STRING_FILTER_TYPES.has(type) && isNonEmptyString(candidate.value)) {
    return { type, value: candidate.value } as ExplorationFilter;
  }

  return null;
}

function parseFilters(raw: string | null): ExplorationFilter[] {
  return safeJsonParseArray<unknown>(raw)
    .map(parseExplorationFilter)
    .filter((filter): filter is ExplorationFilter => filter !== null);
}

export function normalizeExplorationUrlSnapshot(
  snapshot: ExplorationUrlSnapshot
): ExplorationUrlSnapshot {
  const view = ENABLED_VIEW_MODES.has(snapshot.view)
    ? snapshot.view
    : defaultExplorationUrlSnapshot.view;
  const displayMode = ENABLED_DISPLAY_MODES.has(snapshot.displayMode)
    ? snapshot.displayMode
    : defaultExplorationUrlSnapshot.displayMode;

  return {
    ...snapshot,
    view,
    displayMode,
    coverageGrouping:
      view === 'coverage'
        ? snapshot.coverageGrouping
        : defaultExplorationUrlSnapshot.coverageGrouping,
  };
}

export function parseExplorationUrlParams(searchParams: URLSearchParams): ExplorationUrlSnapshot {
  const secondary = parseSecondary(searchParams.get(EXPLORATION_GROUP_PARAM));

  return normalizeExplorationUrlSnapshot({
    view: parseView(searchParams.get(EXPLORATION_VIEW_PARAM)),
    displayMode: parseDisplayMode(searchParams.get(EXPLORATION_DISPLAY_PARAM)),
    secondary,
    coverageGrouping: parseCoverageGrouping(searchParams.get(EXPLORATION_COVERAGE_GROUP_PARAM)),
    tagKey: secondary === 'tag' ? searchParams.get(EXPLORATION_TAG_KEY_PARAM) || '' : '',
    drillPath: parseDrillLocation(searchParams.get(EXPLORATION_DRILL_PARAM)),
    searchQuery: searchParams.get(EXPLORATION_SEARCH_PARAM) || '',
    filters: parseFilters(searchParams.get(EXPLORATION_FILTERS_PARAM)),
    groupByAccount: searchParams.get(EXPLORATION_BY_ACCOUNT_PARAM) === '1',
    selectedVolumeId: searchParams.get(EXPLORATION_VOLUME_PARAM) || null,
    accountId: searchParams.get(EXPLORATION_ACCOUNT_PARAM) || null,
  });
}

export function snapshotFromExplorationState(state: ExplorationState): ExplorationUrlSnapshot {
  return {
    view: state.view,
    displayMode: state.displayMode,
    secondary: state.secondary,
    coverageGrouping: state.coverageGrouping,
    tagKey: state.tagKey,
    drillPath: state.drillPath,
    searchQuery: state.searchQuery,
    filters: state.filters,
    groupByAccount: state.groupByAccount,
    selectedVolumeId:
      state.selectedNode?.kind === 'volume' ? state.selectedNode.volume.ebsId || null : null,
    accountId: state.accountId,
  };
}

function filtersEqual(a: ExplorationFilter[], b: ExplorationFilter[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((filter, index) => JSON.stringify(filter) === JSON.stringify(b[index]));
}

export function snapshotsEqual(a: ExplorationUrlSnapshot, b: ExplorationUrlSnapshot): boolean {
  return (
    a.view === b.view &&
    a.displayMode === b.displayMode &&
    a.secondary === b.secondary &&
    a.coverageGrouping === b.coverageGrouping &&
    a.tagKey === b.tagKey &&
    drillLocationsEqual(a.drillPath, b.drillPath) &&
    a.searchQuery === b.searchQuery &&
    filtersEqual(a.filters, b.filters) &&
    a.groupByAccount === b.groupByAccount &&
    a.selectedVolumeId === b.selectedVolumeId &&
    a.accountId === b.accountId
  );
}

export function classifyUrlSnapshotDelta(
  prev: ExplorationUrlSnapshot,
  next: ExplorationUrlSnapshot
): UrlSnapshotDelta {
  if (snapshotsEqual(prev, next)) return 'none';

  const searchChanged = prev.searchQuery !== next.searchQuery;
  const navigationChanged =
    prev.view !== next.view ||
    prev.displayMode !== next.displayMode ||
    prev.secondary !== next.secondary ||
    prev.coverageGrouping !== next.coverageGrouping ||
    prev.tagKey !== next.tagKey ||
    !drillLocationsEqual(prev.drillPath, next.drillPath) ||
    !filtersEqual(prev.filters, next.filters) ||
    prev.groupByAccount !== next.groupByAccount ||
    prev.selectedVolumeId !== next.selectedVolumeId ||
    prev.accountId !== next.accountId;

  if (searchChanged && !navigationChanged) return 'search-only';
  return 'navigation';
}

function setOrDelete(params: URLSearchParams, key: string, value: string | null) {
  if (value) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
}

export function buildExplorationSearchParams(
  prev: URLSearchParams,
  snapshot: ExplorationUrlSnapshot,
  options: BuildExplorationSearchParamsOptions = {}
): URLSearchParams {
  const normalized = normalizeExplorationUrlSnapshot(snapshot);
  const params = new URLSearchParams(prev);
  const defaultAccountId = options.defaultAccountId ?? null;

  setOrDelete(
    params,
    EXPLORATION_VIEW_PARAM,
    normalized.view === defaultExplorationUrlSnapshot.view ? null : normalized.view
  );
  setOrDelete(
    params,
    EXPLORATION_DISPLAY_PARAM,
    normalized.displayMode === defaultExplorationUrlSnapshot.displayMode
      ? null
      : normalized.displayMode
  );
  setOrDelete(
    params,
    EXPLORATION_GROUP_PARAM,
    normalized.secondary === defaultExplorationUrlSnapshot.secondary ? null : normalized.secondary
  );
  setOrDelete(
    params,
    EXPLORATION_COVERAGE_GROUP_PARAM,
    normalized.view === 'coverage' &&
      normalized.coverageGrouping !== defaultExplorationUrlSnapshot.coverageGrouping
      ? normalized.coverageGrouping
      : null
  );
  setOrDelete(
    params,
    EXPLORATION_TAG_KEY_PARAM,
    normalized.secondary === 'tag' && normalized.tagKey ? normalized.tagKey : null
  );
  setOrDelete(
    params,
    EXPLORATION_BY_ACCOUNT_PARAM,
    normalized.groupByAccount ? '1' : null
  );
  setOrDelete(
    params,
    EXPLORATION_SEARCH_PARAM,
    normalized.searchQuery ? normalized.searchQuery : null
  );
  setOrDelete(
    params,
    EXPLORATION_DRILL_PARAM,
    serializeDrillLocation(normalized.drillPath)
  );
  setOrDelete(
    params,
    EXPLORATION_FILTERS_PARAM,
    normalized.filters.length > 0 ? JSON.stringify(normalized.filters) : null
  );
  setOrDelete(params, EXPLORATION_VOLUME_PARAM, normalized.selectedVolumeId);
  setOrDelete(
    params,
    EXPLORATION_ACCOUNT_PARAM,
    normalized.accountId && normalized.accountId !== defaultAccountId
      ? normalized.accountId
      : null
  );

  return params;
}

export function resolveExplorationUrlSnapshot(
  searchParams: URLSearchParams,
  accounts: Record<string, { volumes: unknown[] }>
): ExplorationUrlSnapshot {
  const parsed = parseExplorationUrlParams(searchParams);
  if (parsed.accountId && !accounts[parsed.accountId]) {
    return { ...parsed, accountId: null };
  }
  return parsed;
}
